import { Inject, Injectable } from "@angular/core";
import { NavigationService } from "@tylertech/core-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { IAggregateActionHandler, AggregateHelperService, AggregateActionResult } from "@tylertech/aggregates-module";
import { IShellService } from "@tylertech/incode-module";
import { IAggregate, ICardData, ICardAction, ExecutionResult } from "@tylertech/tyler-api-types";

@Injectable()
export class TylerSmartMeterActionHandler implements IAggregateActionHandler
{
    constructor(
        private readonly $navigationService: NavigationService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $shellService: IShellService,
        private readonly $aggregateHelperService: AggregateHelperService)
    { }

    predicate(aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): boolean
    {
        return this.isLeakAlertsService(aggregate)
            || this.isActiveAccountsWithoutUsageService(aggregate)
            || this.isConsumptionThresholdAlertsService(aggregate)
            || this.isEstimatedBillAlerts(aggregate)
            || this.isLeakNotificationNotSentService(aggregate)
            || this.isReverseFlowAlertsService(aggregate)
            || this.isUsageSpikeAlertsService(aggregate)
            || this.isActiveMetersNotReportingService(aggregate);
    }

    private isActiveAccountsWithoutUsageService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.ActiveAccountsWithoutUsageService";
    }

    private isConsumptionThresholdAlertsService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.ConsumptionThresholdAlertsService";
    }

    private isEstimatedBillAlerts(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.EstimatedBillAlerts";
    }

    private isLeakNotificationNotSentService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.LeakNotificationNotSentService";
    }

    private isReverseFlowAlertsService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.ReverseFlowAlertsService";
    }

    private isUsageSpikeAlertsService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.UsageSpikeAlertsService";
    }

    private isLeakAlertsService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.LeakAlertsService";
    }

    private isActiveMetersNotReportingService(aggregate: IAggregate): boolean
    {
        return aggregate.AggregateProviderId === "Utility.ActiveMetersNotReportingService";
    }

    execute(aggregate: IAggregate, cardData: ICardData, action: ICardAction): Promise<AggregateActionResult>
    {
        if (action.Id === "AccountResearchNewTabAction")
        {
            this.launchAccountResearch(aggregate, cardData, action);
        }
        if (action.Id === "Dismiss")
        {
            this.dismissAggregate(aggregate, cardData, action);
        }
        return Promise.resolve(true);
    }

    private launchAccountResearch(aggregate: IAggregate, cardData: ICardData, action: ICardAction): void
    {
        if (!action.content)
        {
            return;
        }

        const meterNumber = this.$aggregateHelperService.getFieldValue(cardData, "MeterNumber");
        const meterGroupId = this.$aggregateHelperService.getFieldValue(cardData, "MeterGroupId");
        const miuId = this.$aggregateHelperService.getFieldValue(cardData, "MIU");
        const alertProcessingResultId = this.$aggregateHelperService.getFieldValue(cardData, "AlertProcessingResultId");

        const input = JSON.stringify(
            this.isLeakNotificationNotSentService(aggregate)
                ? [
                    meterNumber,
                    meterGroupId,
                    miuId,
                    alertProcessingResultId,
                    "true"
                ]
                : [
                    meterNumber,
                    meterGroupId,
                    miuId,
                    alertProcessingResultId
                ]);

        if (this.$shellService.isEmbedded())
        {
            this.$shellService.openShellFunction(action.content, input, false, "Failed to launch Account Research")
                .then(() => window.location.reload());

            return;
        }
        const queryParams: Record<string, string> = {
            meterNumber: meterNumber,
            meterGroupId: meterGroupId,
            meterRemoteReadId: miuId,
            alertProcessingResultId: alertProcessingResultId,
        };

        if (this.isLeakNotificationNotSentService(aggregate))
        {
            queryParams["resendAlert"] = "true";
        }

        this.$navigationService.openApp("accountresearch/", "_blank", queryParams);

        window.location.reload();
    }

    private dismissAggregate(aggregate: IAggregate, cardData: ICardData, action: ICardAction): void
    {
        const json = JSON.stringify({
            AlertProcessingResultId: this.$aggregateHelperService.getFieldValue(cardData, "AlertProcessingResultId"),
        });

        (this.$shellService.isEmbedded()
            ? this.$shellService.executeShellFunction(action.content, json, false)
            : this.$aggregateHelperService.performServerCardAction(aggregate, cardData, action))
            .catch(
                // TODO - there is no guarantee that the rejection argument will be of this type. Rejection handler parameters need to be `unknown` and we have to test for the type to correctly handle it.
                (result: ExecutionResult<string>) =>
                {
                    return this.$commonDialogsService.showAlertHtmlMessage(
                        "Failed to dismiss.<br>" + result.Result,
                        "Error");
                });

    }

    load(_aggregate: IAggregate, _cardData: ICardData, _action: ICardAction): void
    {
        /* NO_OP */
    }
}
